/**
 * Enhanced Patient Overview Component
 * Premium UX, Production-Grade, Therapist/Psychiatrist Focused
 * Reusable across roles (Doctor, Receptionist, Patient)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Phone,
  Email,
  CalendarToday,
  AccessTime,
  LocalHospital,
  Medication,
  Psychology,
  Assessment,
  Warning,
  CheckCircle,
  Schedule,
  Person,
  Notes,
  Description,
  History,
  Favorite,
  FavoriteBorder,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { format, formatDistanceToNow, isBefore, subDays } from 'date-fns';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  textAlign: 'center',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.1),
    transform: 'scale(1.02)',
  },
}));

const RiskIndicator = styled(Chip)(({ theme, risklevel }) => {
  const colors = {
    low: { bg: theme.palette.success.main, text: theme.palette.success.contrastText },
    medium: { bg: theme.palette.warning.main, text: theme.palette.warning.contrastText },
    high: { bg: theme.palette.error.main, text: theme.palette.error.contrastText },
    critical: { bg: theme.palette.error.dark, text: theme.palette.error.contrastText },
  };
  const color = colors[risklevel] || colors.low;
  
  return {
    backgroundColor: alpha(color.bg, 0.1),
    color: color.bg,
    border: `1px solid ${alpha(color.bg, 0.3)}`,
    fontWeight: 600,
  };
});

const EnhancedPatientOverview = ({ patient, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  
  // Get patient data
  const patientData = patient?.patient || patient || {};
  const stats = patient?.stats || {};
  const appointments = patient?.appointments || [];
  
  // Calculate key metrics
  const metrics = useMemo(() => {
    const totalAppointments = stats.total_appointments || 0;
    const completedAppointments = stats.completed_appointments || 0;
    const upcomingAppointments = stats.upcoming_appointments || 0;
    const lastAppointmentDate = stats.last_appointment_date 
      ? new Date(stats.last_appointment_date)
      : null;
    const daysSinceLastVisit = lastAppointmentDate 
      ? Math.floor((new Date() - lastAppointmentDate) / (1000 * 60 * 60 * 24))
      : null;
    
    // Calculate adherence rate (if we have medication/compliance data)
    const adherenceRate = patient?.medication_adherence || 0;
    
    // Calculate risk level based on various factors
    let riskLevel = 'low';
    if (daysSinceLastVisit > 90) riskLevel = 'medium';
    if (daysSinceLastVisit > 180) riskLevel = 'high';
    if (adherenceRate < 50) riskLevel = riskLevel === 'high' ? 'critical' : 'high';
    if (patientData?.emergency_flag) riskLevel = 'critical';
    
    return {
      totalAppointments,
      completedAppointments,
      upcomingAppointments,
      lastAppointmentDate,
      daysSinceLastVisit,
      adherenceRate,
      riskLevel,
      completionRate: totalAppointments > 0 
        ? Math.round((completedAppointments / totalAppointments) * 100)
        : 0,
    };
  }, [patient, stats]);

  // Quick Actions
  const quickActions = [
    { 
      label: 'Start Consultation', 
      icon: <Psychology />, 
      color: 'primary',
      path: `/doctor-dashboard/enhanced-consultation?patientId=${patientData?.id}`,
      visible: role === 'doctor'
    },
    { 
      label: 'Add Note', 
      icon: <Notes />, 
      color: 'info',
      path: `#clinical-notes`,
      visible: role === 'doctor'
    },
    { 
      label: 'Schedule Appointment', 
      icon: <Schedule />, 
      color: 'success',
      path: `/doctor-dashboard/appointments?action=create&patientId=${patientData?.id}`,
      visible: ['doctor', 'receptionist'].includes(role)
    },
    { 
      label: 'View History', 
      icon: <History />, 
      color: 'default',
      path: `#treatment-history`,
      visible: true
    },
  ].filter(action => action.visible);

  return (
    <Box>
      <AnimatePresence>
        {/* Risk Alert */}
        {metrics.riskLevel !== 'low' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Alert 
              severity={metrics.riskLevel === 'critical' ? 'error' : 'warning'}
              icon={<Warning />}
              sx={{ mb: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" fontWeight={600}>
                  Patient Risk Level: {metrics.riskLevel.toUpperCase()}
                </Typography>
                {metrics.daysSinceLastVisit > 90 && (
                  <Chip 
                    label={`${metrics.daysSinceLastVisit} days since last visit`}
                    size="small"
                    color={metrics.daysSinceLastVisit > 180 ? 'error' : 'warning'}
                  />
                )}
                {metrics.adherenceRate < 50 && (
                  <Chip 
                    label={`Adherence: ${metrics.adherenceRate}%`}
                    size="small"
                    color="error"
                  />
                )}
              </Box>
            </Alert>
          </motion.div>
        )}

        {/* Key Metrics Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Appointments
                    </Typography>
                    <CalendarToday color="primary" />
                  </Box>
                  <Typography variant="h4" fontWeight={600}>
                    {metrics.totalAppointments}
                  </Typography>
                  {metrics.completionRate > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={metrics.completionRate}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {metrics.completionRate}% completion rate
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Upcoming
                    </Typography>
                    <Schedule color="info" />
                  </Box>
                  <Typography variant="h4" fontWeight={600} color="info.main">
                    {metrics.upcomingAppointments}
                  </Typography>
                  {metrics.upcomingAppointments > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Next: {appointments.find(a => a.status === 'Scheduled')?.scheduled_date 
                        ? format(new Date(appointments.find(a => a.status === 'Scheduled').scheduled_date), 'MMM dd')
                        : 'Not scheduled'}
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Visit
                    </Typography>
                    <History color="secondary" />
                  </Box>
                  {metrics.lastAppointmentDate ? (
                    <>
                      <Typography variant="h6" fontWeight={600}>
                        {formatDistanceToNow(metrics.lastAppointmentDate, { addSuffix: true })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(metrics.lastAppointmentDate, 'MMM dd, yyyy')}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No visits yet
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Risk Level
                    </Typography>
                    <Assessment color={metrics.riskLevel === 'critical' ? 'error' : 'warning'} />
                  </Box>
                  <RiskIndicator 
                    label={metrics.riskLevel.toUpperCase()}
                    risklevel={metrics.riskLevel}
                    size="small"
                  />
                  {metrics.adherenceRate > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Adherence: {metrics.adherenceRate}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={metrics.adherenceRate}
                        color={metrics.adherenceRate >= 80 ? 'success' : metrics.adherenceRate >= 50 ? 'warning' : 'error'}
                        sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {quickActions.map((action, index) => (
            <Grid item key={action.label}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="outlined"
                  startIcon={action.icon}
                  color={action.color}
                  sx={{ borderRadius: 2 }}
                >
                  {action.label}
                </Button>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Patient Information Cards */}
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone color="primary" />
                      <Typography>{patientData?.phone_number || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Email color="primary" />
                      <Typography>{patientData?.email || 'N/A'}</Typography>
                    </Box>
                    {patientData?.address && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Person color="primary" />
                        <Typography>{patientData.address}</Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Recent Activity
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {appointments.length > 0 ? (
                    <Stack spacing={1.5}>
                      {appointments.slice(0, 3).map((appt, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip 
                            label={appt.status}
                            size="small"
                            color={
                              appt.status === 'Completed' ? 'success' :
                              appt.status === 'Scheduled' ? 'info' :
                              'default'
                            }
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {appt.scheduled_date ? format(new Date(appt.scheduled_date), 'MMM dd, yyyy') : 'N/A'}
                            </Typography>
                            {appt.reason && (
                              <Typography variant="caption" color="text.secondary">
                                {appt.reason}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No recent activity
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>
        </Grid>
      </AnimatePresence>
    </Box>
  );
};

export default EnhancedPatientOverview;

